import { useState, type FormEvent } from 'react';
import api, { ApiError } from '../lib/apiClient';
import { IconButton } from '../components/IconButton';
import { IconView } from '../components/icons';
import Pagination from '../components/Pagination';
import { useAuth } from '../auth/useAuth';
import { useConfig } from '../context/ConfigContext';
import { useToast } from '../context/ToastContext';
import { formatMoney } from '../utils/format';
import usePaginatedList from '../hooks/usePaginatedList';

interface BatchRow {
  id: number;
  batch_number: string;
  batch_date: string;
  status: string;
  total_debit: string;
  lines_count: number;
}

const STATUS_LABELS: Record<string, string> = {
  generated: 'Generated',
  failed: 'Failed',
  exported: 'Exported',
  completed: 'Completed',
};

const STATUS_BADGE: Record<string, string> = {
  generated: 'badge-scheduled',
  failed: 'badge-urgent',
  exported: 'badge-neutral',
  completed: 'badge-active',
};

const NO_FILTERS = {};

export function GlBatches() {
  const { can } = useAuth();
  const { settings } = useConfig();
  const { showError, showSuccess } = useToast();
  const [generating, setGenerating] = useState(false);
  const { data, loading, error, setPage, reload } = usePaginatedList<BatchRow>('/gl-batches', NO_FILTERS);

  const canCreate = can('gl_interface.create');
  const fmtMoney = (n: string) => formatMoney(Number(n), settings.currency);

  const generate = async (e: FormEvent) => {
    e.preventDefault();
    setGenerating(true);
    try {
      const res = await api.post<{ message?: string; batch_number?: string }>('/gl-batches/generate');
      showSuccess(res.status === 201 ? `Batch ${res.data.batch_number} generated.` : (res.data.message ?? 'No new finalised transactions to extract.'));
      reload();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Batch generation failed.');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <>
      <header className="page-header">
        <h1>GL Daily Batches</h1>
        {canCreate && (
          <button type="button" className="btn btn-primary" onClick={generate} disabled={generating}>
            {generating ? 'Generating…' : 'Generate Now'}
          </button>
        )}
      </header>
      <p className="muted">
        Extracted from finalised contributions, penalties, and benefit payments. Runs automatically overnight — this
        button triggers the exact same run on demand.
      </p>

      <div className="card">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Batch Number</th>
                <th>Date</th>
                <th>Lines</th>
                <th>Total Value</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="muted center">
                    Loading…
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={6} className="error center">
                    {error}
                  </td>
                </tr>
              ) : data && data.data.length > 0 ? (
                data.data.map((batch) => (
                  <tr key={batch.id}>
                    <td className="muted">{batch.batch_number}</td>
                    <td className="muted">{batch.batch_date}</td>
                    <td>{batch.lines_count}</td>
                    <td>{fmtMoney(batch.total_debit)}</td>
                    <td>
                      <span className={`badge ${STATUS_BADGE[batch.status] ?? 'badge-neutral'}`}>
                        {STATUS_LABELS[batch.status] ?? batch.status}
                      </span>
                    </td>
                    <td className="right">
                      <IconButton icon={IconView} label="View" to={`/gl/batches/${batch.id}`} />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="muted center">
                    No batches yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination meta={data} onPage={setPage} />
      </div>
    </>
  );
}
